/**
 * Atlas focus requests (Tome UI Round 3, the next program's E2): another window asks the World Atlas
 * to show a place, the atlas selects and centres it when it opens or is already open. A request only
 * highlights a destination; it never travels, accepts or rewards anything. Places are the atlas's own
 * (buildWorldMap: drafts and pocket maps are not on it), so a link is offered only where the atlas
 * can show it.
 */
import { useSyncExternalStore } from 'react';
import { ZONE_CONFIGS, getZoneConfig } from '../../../../shared/content/generated/zones.gen';
import { LevelRegistry } from '../../../systems/map/LevelRegistry';
import { windowBridge } from '../../framework/windowBridge';
import { WORLD_MAP_LAYOUT } from './worldMapLayout';
import { buildWorldMap } from './worldMapModel';

export interface AtlasFocusRequest { mapId: string; seq: number }

let request: AtlasFocusRequest | null = null;
const listeners = new Set<() => void>();

/** Asks the atlas to show `mapId`, and opens it. */
export function showOnAtlas(mapId: string): void {
  request = { mapId, seq: (request?.seq ?? 0) + 1 };
  listeners.forEach((fn) => fn());
  windowBridge.open('worldMap');
}

/** The atlas took the request: a later open shows the current map again. */
export function consumeAtlasFocus(): void {
  if (!request) return;
  request = null;
  listeners.forEach((fn) => fn());
}

/** The pending focus request, for the atlas to act on and consume. */
export function useAtlasFocusRequest(): AtlasFocusRequest | null {
  return useSyncExternalStore((fn) => { listeners.add(fn); return () => listeners.delete(fn); }, () => request, () => null);
}

let places: Map<string, string> | null = null;
/** The atlas's places, id → display name (built once, as the atlas builds them). */
function atlasPlaces(): Map<string, string> {
  if (!places) {
    const registry = LevelRegistry.getInstance();
    places = new Map(buildWorldMap(WORLD_MAP_LAYOUT, (id) => registry.getLevel(id), getZoneConfig).places.map((p) => [p.id, p.name]));
  }
  return places;
}

export interface AtlasPlace { mapId: string; name: string }

/** The atlas place for a map, or null when the atlas does not show it. */
export function atlasPlace(mapId: string): AtlasPlace | null {
  const name = atlasPlaces().get(mapId);
  return name === undefined ? null : { mapId, name };
}

/** The atlas place an NPC stands in, or null when it stands on no map the atlas shows. */
export function npcAtlasPlace(npcId: string | null | undefined): AtlasPlace | null {
  if (!npcId) return null;
  for (const [zoneId, zone] of Object.entries(ZONE_CONFIGS)) {
    const place = zone.npcSpawns.some((s) => s.npcId === npcId) ? atlasPlace(zoneId) : null;
    if (place) return place;
  }
  return null;
}

/** For tests: forget the cached places and the last request. */
export function resetAtlasFocusForTests(): void {
  places = null;
  request = null;
}
